import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { isEmpty } from 'lodash';
import BaseTable, { SortOrder } from 'react-base-table';
import { useSelector, useDispatch } from 'react-redux';
import { useAtomValue } from 'jotai';
import { filterModeAtom } from '@src/atoms/page';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { metricFilterRulesSelector } from '@store/trends/base/selectors';
import { filterSelector } from '@store/common/selectors';
import { Retry } from '@components/common';
import selector from '@store/appViewerSessions/selectors';
import { createFetchData } from '@store/appViewerSessions/actions';
import SearchTool from './SearchTool';
import CustomCell from './CustomCell';
import Empty from './Empty';
import { selectedMetricsAtom, anchorMetricsAtom, allMetricsAtom } from '../atoms';
import { UserSessionsTableWrapper, UserSessionsTableHeader } from './styled';
import { getSegregatedMetrics, getSortAndSearchTable, filteredDimensions } from '../utils';
import userSessionExport from './userSessionExport';
import { doesFilterContainsInvalidField } from '@src/hooks/useSetError';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';

const UserSessionsTable = ({ interval }) => {
  const dispatch = useDispatch();
  const { response, isFetching, errorCode } = useSelector(selector);
  const allMetrics = useAtomValue(allMetricsAtom);
  const anchoredMetrics = useAtomValue(anchorMetricsAtom);
  const filterMode = useAtomValue(filterModeAtom);
  const selectedMetrics: any = useAtomValue(selectedMetricsAtom);
  const metricFilter = useSelector(metricFilterRulesSelector);
  const globalFilter = useSelector(filterSelector);
  const selectedMetricCardMetric: any = useSelector(selectedMetricSelector);
  const [tableColumns, setTableColumns] = useState([]);
  const [formattedMetricList, setFormattedMetricList] = useState({});
  const [tableData, setTableData] = useState([]);
  const { dataScope } = useSelector(activeDashboardSelector);
  const sortBy = useMemo(
    () => [
      {
        metric: selectedMetricCardMetric,
        order: 'desc',
      },
    ],
    [selectedMetricCardMetric],
  );

  const [columnSearchData, setColumnSearchData] = useState({
    text: '',
    key: '',
  });

  const [columnSortData, setColumnSortData] = useState<{
    order: SortOrder;
    key: string;
  }>({
    order: 'desc',
    key: selectedMetricCardMetric,
  });

  const onSearch = useCallback((data) => {
    setColumnSearchData(data);
  }, []);

  useEffect(() => {
    setColumnSortData({
      order: 'desc',
      key: selectedMetricCardMetric,
    });
  }, [selectedMetricCardMetric]);

  useEffect(() => {
    if (!isEmpty(allMetrics)) {
      const allMetricList = {};
      Object.keys(allMetrics).map((key) => {
        allMetrics[key].map((item) => {
          allMetricList[item.metric] = item;
        });
      });
      setFormattedMetricList(allMetricList);
    }
  }, [allMetrics]);

  useEffect(() => {
    if (!selectedMetrics.includes(columnSortData.key)) {
      setColumnSortData({
        order: 'desc',
        key: selectedMetricCardMetric,
      });
      setColumnSearchData({
        text: '',
        key: '',
      });
    }
  }, [selectedMetrics]);

  useEffect(() => {
    const formattedTableData = getSortAndSearchTable(
      response,
      columnSearchData,
      columnSortData,
      selectedMetrics,
    );
    setTableData(formattedTableData);
  }, [response, columnSortData, columnSearchData, selectedMetrics]);

  useEffect(() => {
    if (!isEmpty(formattedMetricList)) {
      const segregatedMetrics = getSegregatedMetrics(selectedMetrics, formattedMetricList);

      const dimensions = filteredDimensions(segregatedMetrics.dimensions, filterMode);
      dispatch(
        createFetchData({
          interval,
          metrics: segregatedMetrics.metrics,
          metricFilter,
          filter: globalFilter,
          dimensions,
          sortBy,
          dataScope,
        }),
      );
    }
  }, [
    interval,
    selectedMetrics,
    metricFilter,
    globalFilter,
    formattedMetricList,
    dispatch,
    sortBy,
    dataScope,
  ]);

  const handleRetryRequest = useCallback(() => {
    if (!isEmpty(formattedMetricList)) {
      const segregatedMetrics = getSegregatedMetrics(selectedMetrics, formattedMetricList);
      dispatch(
        createFetchData({
          interval,
          metrics: selectedMetrics,
          metricFilter,
          filter: globalFilter,
          dimensions: segregatedMetrics.dimensions,
          sortBy,
        }),
      );
    }
  }, [
    dispatch,
    formattedMetricList,
    interval,
    selectedMetrics,
    metricFilter,
    globalFilter,
    sortBy,
  ]);

  userSessionExport({ data: tableData, columns: tableColumns, interval });

  const headerRender = useCallback(
    ({ cells, columns: tableColumns }) =>
      tableColumns.map((item, index) => (
        <UserSessionsTableHeader key={item.title} title={item.title}>
          {cells[index]}
          <SearchTool
            currentKey={item.dataKey}
            columnSearchData={columnSearchData}
            onSearch={onSearch}
          />
        </UserSessionsTableHeader>
      )),
    [columnSearchData, onSearch],
  );

  const columnSort = (data) => {
    const { key, order } = data;
    setColumnSortData({
      order,
      key,
    });
  };

  useEffect(() => {
    if (!isEmpty(formattedMetricList)) {
      const generateNewColumns = (metricArray) =>
        metricArray.map((item) => ({
          dataKey: item,
          frozen: anchoredMetrics[item] ? 'left' : false,
          title: formattedMetricList[item] ? formattedMetricList[item].name : '',
          unit: formattedMetricList[item]
            ? formattedMetricList[item].unit || 'unitless'
            : 'unitless',
          width: 200,
          height: 60,
          resizable: true,
          sortable: true,
          maxWidth: 340,
          minWidth: 60,
          key: item,
        }));
      const columnsData = generateNewColumns(selectedMetrics);
      setTableColumns(columnsData);
    }
  }, [anchoredMetrics, selectedMetrics, formattedMetricList]);
  const filterError = doesFilterContainsInvalidField({ rule: globalFilter });
  return (
    <UserSessionsTableWrapper>
      <Retry
        isLoading={isFetching}
        showError={errorCode !== 0}
        fetchData={handleRetryRequest}
        progress={true}
        queryError={filterError}
      >
        <AutoSizer key="viewer-sessions-table">
          {({ width, height }) => (
            <BaseTable
              data={tableData}
              width={width}
              height={height}
              columns={tableColumns}
              fixed
              headerRenderer={headerRender}
              sortBy={columnSortData}
              onColumnSort={columnSort}
              components={{
                TableCell: CustomCell,
              }}
              emptyRenderer={<Empty />}
            />
          )}
        </AutoSizer>
      </Retry>
    </UserSessionsTableWrapper>
  );
};

export default UserSessionsTable;
